import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Email, ContentCopy, Check } from '@mui/icons-material';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useAnimalTypes } from '../../contexts/api-contexts/AnimalTypeContext';
import { useBreeds } from '../../contexts/api-contexts/BreedsContext';
import { useSymptoms } from '../../contexts/api-contexts/SymptomsContext';
import {
  useTreatments,
  Treatment,
} from '../../contexts/api-contexts/TreatmentsContext';
import {
  TreatmentType,
  SelectedTreatment,
  getTreatmentTypeLabel,
} from '../../types/treatment.types';

interface CaseSummaryProps {
  id?: string;
  onNavigate?: (path: string) => void;
  onTabChange?: (
    tab: 'basic' | 'symptoms' | 'diagnoses' | 'treatments' | 'summary'
  ) => void;
}

interface PatientInfoData {
  name: string;
  animalTypeName: string;
  breedName: string;
  genderStr: string;
  neuteredStr: string;
  ageStr: string;
  weightStr: string;
  fullString: string;
}

// Extended treatment with full info
interface TreatmentWithInfo extends SelectedTreatment {
  info?: string;
  follow_up?: string;
}

export const CaseSummary: React.FC<CaseSummaryProps> = ({
  id: _id,
  onNavigate: _onNavigate,
  onTabChange,
}) => {
  const { currentCase } = useCases();
  const { animalTypes } = useAnimalTypes();
  const { breeds, fetchBreedsByAnimalTypeId } = useBreeds();
  const { symptoms: symptomsList } = useSymptoms();
  const { getTreatments, fetchTreatments } = useTreatments();

  const [copied, setCopied] = useState(false);
  const [treatmentsWithInfo, setTreatmentsWithInfo] = useState<
    TreatmentWithInfo[]
  >([]);
  const [loadingTreatmentInfo, setLoadingTreatmentInfo] = useState(false);

  // Fetch breeds when animal type changes
  useEffect(() => {
    if (currentCase?.patient?.animal_type_id) {
      fetchBreedsByAnimalTypeId(currentCase.patient.animal_type_id);
    }
  }, [currentCase?.patient?.animal_type_id, fetchBreedsByAnimalTypeId]);

  // Fetch full treatment info for all selected treatments
  useEffect(() => {
    const fetchTreatmentInfos = async () => {
      if (!currentCase?.selected_treatments?.length) {
        setTreatmentsWithInfo([]);
        return;
      }

      setLoadingTreatmentInfo(true);

      const enrichedTreatments: TreatmentWithInfo[] = [];

      // Group treatments by disease_id and treatment_set_id to minimize API calls
      const treatmentGroups = new Map<
        string,
        {
          diseaseId: number;
          treatmentSetId: number;
          treatments: SelectedTreatment[];
        }
      >();

      for (const st of currentCase.selected_treatments) {
        const key = `${st.disease_id}-${st.treatment_set_id}`;
        if (!treatmentGroups.has(key)) {
          treatmentGroups.set(key, {
            diseaseId: st.disease_id,
            treatmentSetId: st.treatment_set_id,
            treatments: [],
          });
        }
        treatmentGroups.get(key)!.treatments.push(st);
      }

      // Fetch treatments for each group
      for (const group of treatmentGroups.values()) {
        let fullTreatments = getTreatments(
          group.diseaseId,
          group.treatmentSetId
        );
        if (!fullTreatments) {
          fullTreatments = await fetchTreatments(
            group.diseaseId,
            group.treatmentSetId
          );
        }

        // Match selected treatments with full treatment data
        for (const st of group.treatments) {
          const fullTreatment = fullTreatments?.find(
            (t: Treatment) => t.id === st.treatment_id
          );
          enrichedTreatments.push({
            ...st,
            info: fullTreatment?.info,
            follow_up: fullTreatment?.follow_up,
          });
        }
      }

      setTreatmentsWithInfo(enrichedTreatments);
      setLoadingTreatmentInfo(false);
    };

    fetchTreatmentInfos();
  }, [currentCase?.selected_treatments, getTreatments, fetchTreatments]);

  // Get patient information formatted
  const patientInfo = useMemo((): PatientInfoData | null => {
    if (!currentCase?.patient) {
      return null;
    }

    const patient = currentCase.patient;
    const name = patient.name || 'Unnamed';

    // Find animal type name
    let animalTypeName = '';
    if (patient.animal_type_id) {
      const animalType = animalTypes.find(
        (type) => type.id === patient.animal_type_id
      );
      if (animalType) {
        animalTypeName = animalType.name;
      }
    }

    // Find breed name
    let breedName = '';
    if (patient.breed_id && patient.animal_type_id) {
      const breedsList = breeds[patient.animal_type_id];
      if (breedsList) {
        const breed = breedsList.find((b) => b.id === patient.breed_id);
        if (breed) {
          breedName = breed.name;
        }
      }
    }

    // Format gender
    const genderStr =
      patient.gender_id === 1
        ? 'Male'
        : patient.gender_id === 2
        ? 'Female'
        : 'Unknown';

    // Format neutered status
    const neuteredStr =
      patient.neutered === true
        ? 'Neutered'
        : patient.neutered === false
        ? 'Intact'
        : '';

    // Calculate age if date_of_birth is available
    let ageStr = '';
    if (patient.date_of_birth) {
      const birthDate = new Date(patient.date_of_birth);
      const today = new Date();
      const ageInYears = Math.floor(
        (today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
      );
      const ageInMonths = Math.floor(
        (today.getTime() - birthDate.getTime()) / (30.44 * 24 * 60 * 60 * 1000)
      );
      if (ageInYears >= 1) {
        ageStr = `${ageInYears} year${ageInYears !== 1 ? 's' : ''} old`;
      } else {
        ageStr = `${ageInMonths} month${ageInMonths !== 1 ? 's' : ''} old`;
      }
    }

    // Format weight
    const weightStr = patient.weight_kg
      ? `${patient.weight_kg} kg`
      : 'Weight not specified';

    return {
      name,
      animalTypeName,
      breedName,
      genderStr,
      neuteredStr,
      ageStr,
      weightStr,
      fullString: [
        name,
        animalTypeName,
        breedName,
        genderStr,
        neuteredStr,
        weightStr,
      ]
        .filter(Boolean)
        .join(', '),
    };
  }, [currentCase?.patient, animalTypes, breeds]);

  // Get selected symptoms with full info (type, category, full name including parent)
  const selectedSymptomsWithNames = useMemo(() => {
    if (!currentCase?.selected_symptoms || !symptomsList) {
      return [];
    }

    return currentCase.selected_symptoms
      .map((selectedSymptom) => {
        const symptom = symptomsList.find((s) => s.id === selectedSymptom.id);
        if (!symptom) return null;

        // Build full symptom name: "Parent, Symptom" if parent exists, otherwise just "Symptom"
        let fullName = '';
        if (symptom.parent_symptom) {
          fullName = `${symptom.parent_symptom.name}, ${symptom.name}`;
        } else {
          fullName = symptom.name;
        }

        return {
          id: symptom.id,
          name: fullName,
          typeName: symptom.symptom_type?.name || '',
          categoryName: symptom.symptom_type?.category?.name || '',
          significant: selectedSymptom.significant,
        };
      })
      .filter(Boolean);
  }, [currentCase?.selected_symptoms, symptomsList]);

  // Get selected diagnoses
  const selectedDiagnoses = useMemo(() => {
    return currentCase?.selected_diagnoses || [];
  }, [currentCase?.selected_diagnoses]);

  // Generate summary text for copy/email (matching legacy format)
  const generateSummaryText = () => {
    let text = '';

    // Patient section
    text += 'PATIENT\n';
    text += `${patientInfo?.fullString || 'Patient not assigned'}\n\n`;

    // Diagnoses section
    text += 'DIAGNOSE(S)\n';
    if (selectedDiagnoses.length > 0) {
      selectedDiagnoses.forEach((diagnosis) => {
        text += `${diagnosis.name}\n`;
      });
    } else {
      text += 'No diagnoses selected\n';
    }
    text += '\n';

    // Symptoms section
    text += 'SYMPTOM(S)\n';
    if (selectedSymptomsWithNames.length > 0) {
      selectedSymptomsWithNames.forEach((symptom: any) => {
        text += `CATEGORY: ${symptom.categoryName}   SYMPTOM: ${symptom.name}\n`;
      });
    } else {
      text += 'No symptoms selected\n';
    }
    text += '\n';

    // Treatments section
    text += 'TREATMENT(S)\n';
    if (treatmentsWithInfo.length > 0) {
      treatmentsWithInfo.forEach((treatment, index) => {
        text += `Treatment ${index + 1}\n`;
        text += `TYPE: ${getTreatmentTypeLabel(treatment.type)}        `;
        text += `${
          treatment.type === TreatmentType.Drug ? 'DRUG SUBSTANCE' : 'NAME'
        }: ${treatment.name}\n`;

        // Drug dosage info
        if (
          treatment.type === TreatmentType.Drug &&
          treatment.calculated_dosage
        ) {
          const dosage = treatment.calculated_dosage;
          text += `Dose type: ${dosage.dose_type_name || '-'}     `;
          text += `Administration route: ${
            dosage.administration_route_name || '-'
          }\n`;
          text += `Strength: ${dosage.strength || '-'}${
            dosage.strength_unit ? ` ${dosage.strength_unit}` : ''
          }\n`;
          text += `Dose: ${dosage.dose} mg/kg\n`;
          text += `Calculated amount: ${dosage.result}${
            dosage.amount_unit ? ` ${dosage.amount_unit}` : ''
          }\n`;
        } else if (treatment.type === TreatmentType.Drug) {
          text += 'No dosages chosen for this treatment\n';
        }

        text += `INFO: ${treatment.info || '-'}\n`;
        text += `FOLLOW UP: ${treatment.follow_up || '-'}\n\n`;
      });
    } else {
      text += 'No treatments selected\n';
    }

    return text;
  };

  // Copy summary to clipboard
  const handleCopyToClipboard = async () => {
    const text = generateSummaryText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Send summary via email
  const handleSendEmail = () => {
    const text = generateSummaryText();
    const subject = encodeURIComponent(
      `Case Summary - ${patientInfo?.name || 'Patient'}`
    );
    const body = encodeURIComponent(text);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Check if we have all required data
  const hasPatient = !!currentCase?.patient;
  const hasSymptoms =
    currentCase?.selected_symptoms && currentCase.selected_symptoms.length > 0;
  const hasDiagnoses =
    currentCase?.selected_diagnoses &&
    currentCase.selected_diagnoses.length > 0;
  const hasTreatments = treatmentsWithInfo.length > 0;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pb: 2 }}>
      {/* Header with actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => onTabChange?.('treatments')}
            size="small"
            color="primary"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">
            Summary
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton onClick={handleCopyToClipboard} color="primary">
              {copied ? <Check /> : <ContentCopy />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Send via email">
            <IconButton onClick={handleSendEmail} color="primary">
              <Email />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Patient Information */}
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              PATIENT
            </Typography>
            {hasPatient && patientInfo ? (
              <Typography variant="body2">{patientInfo.fullString}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No patient information available
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Diagnoses */}
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              DIAGNOSES
            </Typography>
            {hasDiagnoses ? (
              <Box>
                {selectedDiagnoses.map((diagnosis, index) => (
                  <Typography key={diagnosis.id || index} variant="body2">
                    {diagnosis.name}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No diagnoses selected
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Symptoms */}
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              SYMPTOMS
            </Typography>
            {hasSymptoms ? (
              <Box>
                {/* Header row */}
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                    >
                      TYPE
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                    >
                      CATEGORY
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                    >
                      SYMPTOM
                    </Typography>
                  </Grid>
                </Grid>
                {/* Symptom rows */}
                {selectedSymptomsWithNames.map((symptom: any, index) => (
                  <Grid
                    container
                    spacing={1}
                    key={symptom.id || index}
                    sx={{ mb: 0.5 }}
                  >
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        {symptom.typeName}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        {symptom.categoryName}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">{symptom.name}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No symptoms selected
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Treatments */}
        <Card variant="outlined" sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              TREATMENTS
            </Typography>
            {loadingTreatmentInfo ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : hasTreatments ? (
              <Box>
                {treatmentsWithInfo.map((treatment, index) => (
                  <Box
                    key={`${treatment.treatment_id}-${index}`}
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom:
                        index < treatmentsWithInfo.length - 1
                          ? '1px solid'
                          : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Treatment {index + 1}
                    </Typography>

                    {/* Type and Name row */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={4}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                        >
                          TYPE
                        </Typography>
                        <Typography variant="body2">
                          {getTreatmentTypeLabel(treatment.type)}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                        >
                          {treatment.type === TreatmentType.Drug
                            ? 'DRUG SUBSTANCE'
                            : 'NAME'}
                        </Typography>
                        <Typography variant="body2">
                          {treatment.name}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Drug dosage info */}
                    {treatment.type === TreatmentType.Drug && (
                      <Box sx={{ mt: 1 }}>
                        {treatment.calculated_dosage ? (
                          <>
                            <Grid container spacing={1} sx={{ mb: 0.5 }}>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                                >
                                  Dose type
                                </Typography>
                                <Typography variant="body2">
                                  {treatment.calculated_dosage.dose_type_name ||
                                    '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                                >
                                  Administration route
                                </Typography>
                                <Typography variant="body2">
                                  {treatment.calculated_dosage
                                    .administration_route_name || '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                                >
                                  Strength
                                </Typography>
                                <Typography variant="body2">
                                  {treatment.calculated_dosage.strength || '-'}
                                  {treatment.calculated_dosage.strength &&
                                  treatment.calculated_dosage.strength_unit
                                    ? ` ${treatment.calculated_dosage.strength_unit}`
                                    : ''}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid container spacing={1} sx={{ mb: 0.5 }}>
                              <Grid item xs={4}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                                >
                                  Dose
                                </Typography>
                                <Typography variant="body2">
                                  {treatment.calculated_dosage.dose} mg/kg
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                                >
                                  Calculated amount
                                </Typography>
                                <Typography variant="body2">
                                  {treatment.calculated_dosage.result}
                                  {treatment.calculated_dosage.amount_unit
                                    ? ` ${treatment.calculated_dosage.amount_unit}`
                                    : ''}
                                </Typography>
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: 'italic' }}
                          >
                            No dosages chosen for this treatment
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Info */}
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                      >
                        INFO
                      </Typography>
                      <Typography variant="body2">
                        {treatment.info || '-'}
                      </Typography>
                    </Box>

                    {/* Follow up */}
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                      >
                        FOLLOW UP
                      </Typography>
                      <Typography variant="body2">
                        {treatment.follow_up || '-'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No treatments selected
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyToClipboard}
            fullWidth
          >
            {copied ? 'Copied!' : 'Copy Summary'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Email />}
            onClick={handleSendEmail}
            fullWidth
          >
            Send via Email
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseSummary;
